import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadIcon, CloseIcon } from "../../Assest/icons/icons";
import uploads from "../../utils/uploads";

const UploadMediaModal = ({ handleClose, show, UNO_URL, moduleUUID, onMediaUpload = () => {}, uploadedMedia = [] }) => {
  const [mediaList, setMediaList] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // 🟢 Initialize from existing uploadedMedia
  useEffect(() => {
    if (uploadedMedia?.length) {
      const existingFiles = uploadedMedia.map((url, index) => ({
        id: `existing-${index}`,
        url,
        name: url?.split("/").pop(),
        size: null,
        status: "done",
        isExisting: true,
      }));
      setMediaList(existingFiles);
    } else {
      setMediaList([]);
    }
  }, [uploadedMedia]);

  const closeModal = () => {
    if (typeof handleClose === "function") handleClose();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
  };

  const handleFiles = (files) => {
    Array.from(files).forEach(async (file) => {
      const id = Date.now() + Math.random();

      // Temporary preview
      const localUrl = URL.createObjectURL(file);
      setMediaList((prev) => [
        ...prev,
        {
          id,
          url: localUrl,
          name: file.name,
          size: file.size,
          status: "uploading",
          isExisting: false,
        },
      ]);

      try {
        const response = await uploads.uploadFile(UNO_URL, moduleUUID, file);
        const uploadedUrl = response?.data?.url;
        if (uploadedUrl) {
          setMediaList((prev) => prev.map((m) => (m.id === id ? { ...m, url: uploadedUrl, status: "done" } : m)));
        } else {
          throw new Error("Upload failed");
        }
      } catch {
        setMediaList((prev) => prev.map((m) => (m.id === id ? { ...m, status: "error" } : m)));
      }
    });
  };

  // 🟢 Remove file (affects both UI and final callback)
  const removeFile = (fileId) => {
    setMediaList((prev) => prev.filter((file) => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAdd = () => {
    // ✅ Only pass remaining uploaded URLs (done or existing)
    const finalUrls = mediaList.filter((m) => m.status === "done" || m.isExisting).map((m) => m.url);

    onMediaUpload(finalUrls);
    closeModal();
  };

  const isVideo = (url = "") => /\.(mp4|mov|avi|webm|mkv)$/i.test(url);

  return (
    <Modal show={show} centered onHide={closeModal} size="lg" className="uploadMediaModal">
      {/* Header */}
      <div className="customModalHead">
        <div className="filterbtn">
          <div className="btninadjust">
            <UploadIcon />
          </div>
        </div>
        <h6>Upload Media Gallery (Max file size: 10MB)</h6>
      </div>

      {/* Body */}
      <Modal.Body>
        <div className="uploadMediaContent">
          <div
            className={`upload-drop-zone ${dragActive ? "drag-active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInput}
              accept="image/*,video/*"
              multiple
              style={{ display: "none" }}
            />
            <div className="upload-icon">
              <UploadIcon />
            </div>
            <p className="upload-text">Drag & drop files here or click to browse</p>
            <p className="upload-subtext">JPG, PNG, JPEG, MP4, MOV files supported</p>
          </div>

          {mediaList.length > 0 && (
            <div className="uploaded-files-section">
              <div className="section-header">
                <p>Uploaded Files ({mediaList.length})</p>
              </div>
              <div className="uploaded-files-list">
                {mediaList.map((file) => (
                  <div key={file.id} className="uploaded-file-item">
                    <div className="file-preview">
                      {isVideo(file.url) ? (
                        <video src={file.url} controls />
                      ) : (
                        <img src={file.url} alt={file.name || "media"} />
                      )}

                      {!file.isExisting && file.status === "uploading" && (
                        <span className="uploading">Uploading...</span>
                      )}
                      {!file.isExisting && file.status === "error" && <span className="error">Failed</span>}
                    </div>

                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      {file.size && <p className="file-size">{formatFileSize(file.size)}</p>}
                    </div>

                    <button type="button" className="remove-file-btn" onClick={() => removeFile(file.id)}>
                      <CloseIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      {/* Footer */}
      <div className="modalFooterB">
        <button className="cancel" onClick={closeModal}>
          Close
        </button>
        <button className="save" onClick={handleAdd}>
          Add
        </button>
      </div>
    </Modal>
  );
};

export default UploadMediaModal;
